import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

import { createKeyPoseSourcePosePayloadFromBvh } from '../keyPoses/keyPoseSnapshots';
import {
  REPATHING_EVAL_STEPS,
  REPATHING_INSERT_MIN_U_GAP,
  REPATHING_MAX_CONTROLS,
  REPATHING_MIN_CONTROLS,
} from './constants';
import type {
  RepathingControlPoint,
  RepathingCurve,
  RepathingHeadingMode,
  RepathingPoint2d,
  RepathingRoot2dConstraint,
  RepathingRootPathSample,
  RepathingSourceHeading,
} from './types';

const EPS = 1e-6;

interface RepathingSample extends RepathingPoint2d {
  frame: number;
  u: number;
}

interface RepathingArcTable {
  table: Array<RepathingPoint2d & { length: number; u: number }>;
  total: number;
}

export function createRepathingCurveFromBvh(bvh: BVH): RepathingCurve | null {
  const frameCount = getBvhFrameCount(bvh);
  if (frameCount <= 1) {
    return null;
  }

  const lastFrame = frameCount - 1;
  const samples: RepathingSample[] = [];
  const sourceHeadings: RepathingSourceHeading[] = [];
  for (let frame = 0; frame < frameCount; frame += 1) {
    const sourcePose = createKeyPoseSourcePosePayloadFromBvh(bvh, frame);
    if (!sourcePose) {
      continue;
    }

    const u = lastFrame ? frame / lastFrame : 0;
    samples.push({
      frame,
      u,
      x: roundNumber(
        sourcePose.smoothRoot2d[0] ?? sourcePose.rootPosition[0] ?? 0,
      ),
      z: roundNumber(
        sourcePose.smoothRoot2d[1] ?? sourcePose.rootPosition[2] ?? 0,
      ),
    });
    sourceHeadings.push({
      u,
      heading: yawFromAxisAngle(sourcePose.localJointRot[0]),
    });
  }

  if (samples.length < 2) {
    return null;
  }

  const simplified = simplifyRepathingSamples(samples);
  return {
    frameCount,
    sourceFrameCount: frameCount,
    durationSec: Math.max(bvh.clip.duration || frameCount / 24, EPS),
    points: assignRepathingAutoHandles(simplified.map(createRepathingPoint)),
    sourceHeadings,
  };
}

export function cloneRepathingCurve(curve: RepathingCurve): RepathingCurve {
  return {
    ...curve,
    points: curve.points.map(cloneRepathingPoint),
    sourceHeadings: curve.sourceHeadings.map((heading) => ({ ...heading })),
  };
}

export function evaluateRepathingCurve(
  curve: RepathingCurve,
  u: number,
): RepathingPoint2d & { u: number } {
  const points = curve.points;
  if (!points.length) return { x: 0, z: 0, u: 0 };

  const clamped = clamp(Number(u || 0), 0, 1);
  if (points.length === 1 || clamped <= points[0].u) {
    return { x: points[0].x, z: points[0].z, u: clamped };
  }

  const last = points[points.length - 1];
  if (clamped >= last.u) {
    return { x: last.x, z: last.z, u: clamped };
  }

  for (let index = 0; index < points.length - 1; index += 1) {
    const left = points[index];
    const right = points[index + 1];
    if (clamped < left.u || clamped > right.u) continue;

    const span = Math.max(EPS, right.u - left.u);
    return {
      ...cubicRepathingPoint(left, right, (clamped - left.u) / span),
      u: clamped,
    };
  }

  return { x: last.x, z: last.z, u: clamped };
}

export function sampleRepathingCurve(
  curve: RepathingCurve,
  steps = REPATHING_EVAL_STEPS,
) {
  const count = Math.max(2, Math.round(steps));
  return Array.from({ length: count + 1 }, (_, index) =>
    evaluateRepathingCurve(curve, index / count),
  );
}

export function buildRepathingRootPathSamples(
  curve: RepathingCurve,
  headingMode: RepathingHeadingMode,
): RepathingRootPathSample[] {
  const frameCount = Math.max(2, Math.round(curve.frameCount || 2));
  const lastFrame = frameCount - 1;
  const arc = repathingArcTable(curve);

  return Array.from({ length: frameCount }, (_, frame) => {
    const progress = lastFrame ? frame / lastFrame : 0;
    const u = repathingUAtArcProgressFromTable(arc, progress);
    const point = evaluateRepathingCurve(curve, u);
    return {
      frame,
      x: roundNumber(point.x),
      z: roundNumber(point.z),
      heading: roundNumber(
        repathingHeadingForSample(curve, progress, u, headingMode),
      ),
      u,
    };
  });
}

export function buildRepathingRoot2dConstraint(
  curve: RepathingCurve,
  headingMode: RepathingHeadingMode,
): RepathingRoot2dConstraint {
  const samples = buildRepathingRootPathSamples(curve, headingMode);
  return {
    type: 'root2d',
    frame_indices: samples.map((sample) => sample.frame),
    smooth_root_2d: samples.map((sample) => [sample.x, sample.z]),
    global_root_heading: samples.map((sample) => [
      roundNumber(Math.cos(sample.heading)),
      roundNumber(Math.sin(sample.heading)),
    ]),
  };
}

export function smoothRepathingCurve(curve: RepathingCurve) {
  if (curve.points.length <= 2) {
    return cloneRepathingCurve(curve);
  }

  const points = curve.points.map((point, index) => {
    if (index === 0 || index === curve.points.length - 1) {
      return { ...point, inHandle: null, outHandle: null };
    }

    const previous = curve.points[index - 1];
    const next = curve.points[index + 1];
    return {
      ...point,
      x: roundNumber(point.x * 0.5 + (previous.x + next.x) * 0.25),
      z: roundNumber(point.z * 0.5 + (previous.z + next.z) * 0.25),
      inHandle: null,
      outHandle: null,
    };
  });

  return {
    ...cloneRepathingCurve(curve),
    points: assignRepathingAutoHandles(points),
  };
}

export function simplifyRepathingCurve(curve: RepathingCurve) {
  if (curve.points.length <= REPATHING_MIN_CONTROLS) {
    return cloneRepathingCurve(curve);
  }

  const target = Math.max(
    REPATHING_MIN_CONTROLS,
    Math.round(curve.points.length * 0.72),
  );
  const samples = curve.points.map((point, index) => ({
    ...point,
    frame: index,
  }));
  const simplified = simplifyRepathingSamples(samples, {
    minCount: REPATHING_MIN_CONTROLS,
    maxCount: target,
  });

  return {
    ...cloneRepathingCurve(curve),
    points: assignRepathingAutoHandles(simplified.map(createRepathingPoint)),
  };
}

export function moveRepathingPoint(
  curve: RepathingCurve,
  pointId: string,
  nextPosition: RepathingPoint2d,
) {
  const point = curve.points.find((item) => item.id === pointId);
  if (!point) return;

  const dx = roundNumber(nextPosition.x - point.x);
  const dz = roundNumber(nextPosition.z - point.z);
  point.x = roundNumber(nextPosition.x);
  point.z = roundNumber(nextPosition.z);
  if (point.inHandle) {
    point.inHandle.x = roundNumber(point.inHandle.x + dx);
    point.inHandle.z = roundNumber(point.inHandle.z + dz);
  }
  if (point.outHandle) {
    point.outHandle.x = roundNumber(point.outHandle.x + dx);
    point.outHandle.z = roundNumber(point.outHandle.z + dz);
  }
}

export function moveRepathingHandle(
  curve: RepathingCurve,
  pointId: string,
  handleName: 'in' | 'out',
  nextPosition: RepathingPoint2d,
) {
  const point = curve.points.find((item) => item.id === pointId);
  if (!point) return;

  const handle = handleName === 'in' ? point.inHandle : point.outHandle;
  if (!handle) return;

  handle.x = roundNumber(nextPosition.x);
  handle.z = roundNumber(nextPosition.z);
}

export function applyRepathingBrush(
  curve: RepathingCurve,
  center: RepathingPoint2d,
  delta: RepathingPoint2d,
  radius: number,
  strength: number,
) {
  if (Math.hypot(delta.x, delta.z) <= EPS) return;

  const safeRadius = Math.max(0.05, radius);
  for (const control of curve.points) {
    const distance = Math.hypot(control.x - center.x, control.z - center.z);
    if (distance > safeRadius) continue;

    const falloff = (1 - distance / safeRadius) ** 2;
    const dx = delta.x * falloff * strength;
    const dz = delta.z * falloff * strength;
    control.x = roundNumber(control.x + dx);
    control.z = roundNumber(control.z + dz);
    if (control.inHandle) {
      control.inHandle.x = roundNumber(control.inHandle.x + dx);
      control.inHandle.z = roundNumber(control.inHandle.z + dz);
    }
    if (control.outHandle) {
      control.outHandle.x = roundNumber(control.outHandle.x + dx);
      control.outHandle.z = roundNumber(control.outHandle.z + dz);
    }
  }
}

function getBvhFrameCount(bvh: BVH) {
  const firstTrack = bvh.clip.tracks.find((track) => track.times.length > 0);
  return firstTrack?.times.length ?? 0;
}

function createRepathingPoint(sample: Partial<RepathingControlPoint>) {
  return {
    id: sample.id || `path-point-${roundNumber(sample.u ?? 0, 6)}`,
    u: clamp(roundNumber(Number(sample.u || 0), 6), 0, 1),
    x: roundNumber(Number(sample.x || 0)),
    z: roundNumber(Number(sample.z || 0)),
    inHandle: sample.inHandle ? roundPoint(sample.inHandle) : null,
    outHandle: sample.outHandle ? roundPoint(sample.outHandle) : null,
  } satisfies RepathingControlPoint;
}

function cloneRepathingPoint(point: RepathingControlPoint) {
  return {
    ...point,
    inHandle: point.inHandle ? { ...point.inHandle } : null,
    outHandle: point.outHandle ? { ...point.outHandle } : null,
  };
}

function assignRepathingAutoHandles(points: RepathingControlPoint[]) {
  const sorted = points.map(createRepathingPoint).sort((a, b) => a.u - b.u);
  for (let index = 0; index < sorted.length; index += 1) {
    const point = sorted[index];
    const previous = sorted[Math.max(0, index - 1)];
    const next = sorted[Math.min(sorted.length - 1, index + 1)];
    const du = Math.max(EPS, next.u - previous.u);
    const tx = (next.x - previous.x) / du;
    const tz = (next.z - previous.z) / du;

    point.inHandle =
      index > 0
        ? {
            x: roundNumber(point.x - (tx * (point.u - previous.u)) / 3),
            z: roundNumber(point.z - (tz * (point.u - previous.u)) / 3),
          }
        : null;
    point.outHandle =
      index < sorted.length - 1
        ? {
            x: roundNumber(point.x + (tx * (next.u - point.u)) / 3),
            z: roundNumber(point.z + (tz * (next.u - point.u)) / 3),
          }
        : null;
  }

  return sorted;
}

function cubicRepathingPoint(
  left: RepathingControlPoint,
  right: RepathingControlPoint,
  t: number,
) {
  const p0 = left;
  const p1 = left.outHandle || {
    x: left.x + (right.x - left.x) / 3,
    z: left.z + (right.z - left.z) / 3,
  };
  const p2 = right.inHandle || {
    x: right.x - (right.x - left.x) / 3,
    z: right.z - (right.z - left.z) / 3,
  };
  const p3 = right;
  const mt = 1 - t;
  const mt2 = mt * mt;
  const t2 = t * t;
  return {
    x:
      mt2 * mt * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t2 * t * p3.x,
    z:
      mt2 * mt * p0.z + 3 * mt2 * t * p1.z + 3 * mt * t2 * p2.z + t2 * t * p3.z,
  };
}

function repathingArcTable(curve: RepathingCurve): RepathingArcTable {
  const samples = sampleRepathingCurve(
    curve,
    Math.max(REPATHING_EVAL_STEPS, curve.points.length * 16),
  );
  let total = 0;
  const table = samples.map((sample, index) => {
    if (index) {
      total += Math.hypot(
        sample.x - samples[index - 1].x,
        sample.z - samples[index - 1].z,
      );
    }
    return { ...sample, length: total };
  });

  return { table, total };
}

function repathingUAtArcProgressFromTable(
  arc: RepathingArcTable,
  progress: number,
) {
  const { table, total } = arc;
  if (!table.length || total <= EPS) return clamp(progress, 0, 1);

  const target = clamp(progress, 0, 1) * total;
  for (let index = 1; index < table.length; index += 1) {
    const previous = table[index - 1];
    const current = table[index];
    if (target > current.length) continue;

    const span = Math.max(EPS, current.length - previous.length);
    const t = (target - previous.length) / span;
    return previous.u + (current.u - previous.u) * t;
  }

  return table[table.length - 1].u;
}

function repathingHeadingForSample(
  curve: RepathingCurve,
  progress: number,
  u: number,
  headingMode: RepathingHeadingMode,
) {
  const sourceHeading = sourceHeadingAtProgress(curve, progress);
  if (headingMode === 'toPath') {
    return tangentHeadingAtU(curve, u, sourceHeading);
  }

  return sourceHeading;
}

function sourceHeadingAtProgress(curve: RepathingCurve, progress: number) {
  const headings = curve.sourceHeadings;
  const u = clamp(progress, 0, 1);
  if (!headings.length) return 0;
  if (u <= headings[0].u) return headings[0].heading;

  const last = headings[headings.length - 1];
  if (u >= last.u) return last.heading;

  for (let index = 0; index < headings.length - 1; index += 1) {
    const left = headings[index];
    const right = headings[index + 1];
    if (u < left.u || u > right.u) continue;

    const t = (u - left.u) / Math.max(EPS, right.u - left.u);
    return left.heading + angleDelta(left.heading, right.heading) * t;
  }

  return last.heading;
}

function tangentHeadingAtU(curve: RepathingCurve, u: number, fallback: number) {
  const delta = 1 / Math.max(REPATHING_EVAL_STEPS, curve.points.length * 32);
  const before = evaluateRepathingCurve(curve, Math.max(0, u - delta));
  const after = evaluateRepathingCurve(curve, Math.min(1, u + delta));
  const dx = after.x - before.x;
  const dz = after.z - before.z;
  if (Math.hypot(dx, dz) <= EPS) return fallback;

  return Math.atan2(dx, dz);
}

function simplifyRepathingSamples(
  samples: RepathingSample[],
  { minCount = REPATHING_MIN_CONTROLS, maxCount = REPATHING_MAX_CONTROLS } = {},
) {
  if (samples.length <= maxCount) return samples;

  const xs = samples.map((point) => point.x);
  const zs = samples.map((point) => point.z);
  const diagonal = Math.hypot(
    Math.max(...xs) - Math.min(...xs),
    Math.max(...zs) - Math.min(...zs),
  );
  let epsilon = Math.max(0.025, diagonal * 0.004);
  let simplified = samples;

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const keep = new Set([0, samples.length - 1]);
    repathingRdpIndices(samples, 0, samples.length - 1, epsilon, keep);
    simplified = [...keep].sort((a, b) => a - b).map((index) => samples[index]);
    if (simplified.length <= maxCount || simplified.length <= minCount) break;
    epsilon *= 1.45;
  }

  if (simplified.length > maxCount) {
    const picked = new Set([0, simplified.length - 1]);
    for (let index = 1; index < maxCount - 1; index += 1) {
      picked.add(
        Math.round((index * (simplified.length - 1)) / (maxCount - 1)),
      );
    }
    simplified = [...picked]
      .sort((a, b) => a - b)
      .map((index) => simplified[index]);
  }

  if (simplified.length < minCount && samples.length >= minCount) {
    const picked = new Set(simplified.map((point) => point.frame));
    for (let index = 0; index < minCount; index += 1) {
      picked.add(
        samples[Math.round((index * (samples.length - 1)) / (minCount - 1))]
          .frame,
      );
    }
    simplified = samples
      .filter((point) => picked.has(point.frame))
      .slice(0, Math.max(minCount, simplified.length));
  }

  return simplified.sort((a, b) => a.u - b.u);
}

function repathingRdpIndices(
  points: RepathingPoint2d[],
  start: number,
  end: number,
  epsilon: number,
  keep: Set<number>,
) {
  if (end <= start + 1) return;

  let maxDistance = -1;
  let maxIndex = -1;
  for (let index = start + 1; index < end; index += 1) {
    const distance = pointDistanceToSegment(
      points[index],
      points[start],
      points[end],
    );
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = index;
    }
  }

  if (maxDistance > epsilon && maxIndex > start) {
    keep.add(maxIndex);
    repathingRdpIndices(points, start, maxIndex, epsilon, keep);
    repathingRdpIndices(points, maxIndex, end, epsilon, keep);
  }
}

function pointDistanceToSegment(
  point: RepathingPoint2d,
  start: RepathingPoint2d,
  end: RepathingPoint2d,
) {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const lengthSq = dx * dx + dz * dz;
  if (lengthSq <= EPS) return Math.hypot(point.x - start.x, point.z - start.z);

  const t = clamp(
    ((point.x - start.x) * dx + (point.z - start.z) * dz) / lengthSq,
    0,
    1,
  );
  return Math.hypot(point.x - (start.x + dx * t), point.z - (start.z + dz * t));
}

function yawFromAxisAngle(axisAngle?: readonly number[]) {
  if (!axisAngle || axisAngle.length < 3) return 0;

  const angle = Math.hypot(
    axisAngle[0] ?? 0,
    axisAngle[1] ?? 0,
    axisAngle[2] ?? 0,
  );
  if (angle <= EPS) return 0;

  const half = angle / 2;
  const scale = Math.sin(half) / angle;
  const x = (axisAngle[0] ?? 0) * scale;
  const y = (axisAngle[1] ?? 0) * scale;
  const z = (axisAngle[2] ?? 0) * scale;
  const w = Math.cos(half);
  const sinyCosp = 2 * (w * y + z * x);
  const cosyCosp = 1 - 2 * (y * y + x * x);
  return Math.atan2(sinyCosp, cosyCosp);
}

function angleDelta(start: number, end: number) {
  return Math.atan2(Math.sin(end - start), Math.cos(end - start));
}

function roundPoint(point: RepathingPoint2d) {
  return {
    x: roundNumber(point.x),
    z: roundNumber(point.z),
  };
}

function roundNumber(value: number, digits = 5) {
  const scale = 10 ** digits;
  return Math.round((Number.isFinite(value) ? value : 0) * scale) / scale;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
